import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import trainingAuthService from '../services/trainingAuthService';

const ROLES = [
  { value: 'training_coordinator', label: 'Training Coordinator' },
  { value: 'training_document_controller', label: 'Training Document Controller' },
  { value: 'training_reviewer', label: 'Training Reviewer' },
  { value: 'training_admin', label: 'Training Admin' },
];

const RESTRICTED_ROLES = ['training_reviewer', 'training_admin'];

const TrainingRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [empCode, setEmpCode] = useState('');
  const [employeeData, setEmployeeData] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '', email: '', password: '', confirmPassword: '', role: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleCheckEmployee = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await trainingAuthService.checkEmployee(empCode);
      setEmployeeData(data.employee);
      setFormData(prev => ({ ...prev, email: data.employee.email, emp_code: empCode }));
      setSuccess('Employee found! Please complete registration.');
      setStep(2);
    } catch (err) {
      setError(err.error || 'Failed to verify employee code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name || !formData.password || !formData.role) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const data = await trainingAuthService.register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        emp_code: empCode,
        role: formData.role,
      });
      setRegisteredEmail(formData.email);
      setSuccess('Registration successful! Please check your email for the verification code.');
      setStep(3);
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await trainingAuthService.verifyEmail(registeredEmail, verificationCode);
      setSuccess('Email verified! Redirecting to login...');
      setTimeout(() => navigate('/training-login'), 2000);
    } catch (err) {
      setError(err.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await trainingAuthService.resendVerification(registeredEmail);
      setSuccess('Verification code sent! Please check your email.');
    } catch (err) {
      setError(err.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-lg">
            <Card.Header className="text-white text-center py-3"
              style={{ background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)' }}>
              <h3 className="mb-0">
                <i className="fas fa-user-plus me-2"></i>
                Register — Training System
              </h3>
              <small>Equipment Inspection Training System</small>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Progress */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  {['Employee Code', 'Registration', 'Verification'].map((label, i) => (
                    <small key={label} className={step >= i + 1 ? 'text-success fw-bold' : 'text-muted'}>
                      {i + 1}. {label}
                    </small>
                  ))}
                </div>
                <div className="progress" style={{ height: '5px' }}>
                  <div className="progress-bar bg-success" style={{ width: `${(step / 3) * 100}%` }}></div>
                </div>
              </div>

              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

              {/* Step 1 */}
              {step === 1 && (
                <Form onSubmit={handleCheckEmployee}>
                  <Form.Group className="mb-3">
                    <Form.Label>Employee Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your employee code"
                      value={empCode}
                      onChange={(e) => setEmpCode(e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      Enter your registered employee code to verify your identity
                    </Form.Text>
                  </Form.Group>
                  <Button variant="success" type="submit" className="w-100" disabled={loading}>
                    {loading
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                      : <><i className="fas fa-search me-2"></i>Verify Employee Code</>}
                  </Button>
                </Form>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <Form onSubmit={handleRegister}>
                  <Alert variant="info">
                    <strong>Employee Found:</strong> {employeeData?.email}
                    <br />
                    <small>Department: {employeeData?.department || 'N/A'}</small>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="full_name"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="email" value={formData.email} disabled className="bg-light" />
                    <Form.Text className="text-muted">Email is fetched from employee records</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                    <Form.Select name="role" value={formData.role} onChange={handleInputChange} required>
                      <option value="">Select Role</option>
                      {ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </Form.Select>
                    {RESTRICTED_ROLES.includes(formData.role) && (
                      <Form.Text className="text-warning">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        This role requires an authorized email address
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      placeholder="Create a strong password (min 6 characters)"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button variant="success" type="submit" disabled={loading}>
                      {loading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Registering...</>
                        : <><i className="fas fa-user-check me-2"></i>Register</>}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setStep(1)}>
                      <i className="fas fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </Form>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <Form onSubmit={handleVerifyEmail}>
                  <div className="text-center mb-4">
                    <i className="fas fa-envelope-open text-success" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3">Verify Your Email</h5>
                    <p className="text-muted">
                      We've sent a 6-digit verification code to<br />
                      <strong>{registeredEmail}</strong>
                    </p>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-center w-100">Verification Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="text-center fs-4"
                      required
                    />
                  </Form.Group>
                  <div className="d-grid gap-2">
                    <Button variant="success" type="submit" disabled={loading}>
                      {loading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                        : <><i className="fas fa-check-circle me-2"></i>Verify Email</>}
                    </Button>
                    <Button variant="outline-success" onClick={handleResendCode} disabled={loading}>
                      <i className="fas fa-redo me-2"></i>Resend Code
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>

            <Card.Footer className="text-center py-3">
              <small>
                Already have an account?{' '}
                <a href="/training-login" className="text-success fw-bold">Login here</a>
              </small>
            </Card.Footer>
          </Card>

          <div className="text-center mt-3">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              <i className="fas fa-home me-2"></i>Back to Home
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default TrainingRegister;
